import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, AlertCircle, Instagram } from "lucide-react";
import { Nav } from "../components/Nav";
import { track, Events } from "../lib/analytics";
import { PageFooter } from "../components/PageFooter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL = 2000;
const MAX_ATTEMPTS = 8;
const INSTAGRAM_URL = "https://www.instagram.com/kisubikashop";

const Success = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [phase, setPhase] = useState("checking"); // checking | paid | failed | timeout
  const [attempts, setAttempts] = useState(0);
  const [data, setData] = useState(null);
  const trackedPaid = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setPhase("failed");
      return;
    }
    let cancelled = false;
    let count = 0;

    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await axios.get(
          `${API}/checkout/status/${sessionId}`
        );
        setData(data);
        setAttempts((a) => a + 1);
        if (data.payment_status === "paid") {
          setPhase("paid");
          if (!trackedPaid.current) {
            trackedPaid.current = true;
            track(Events.CheckoutPaid, {
              session_id: sessionId,
              reservation_id: data.reservation_id,
              amount_total: data.amount_total,
              currency: data.currency,
            });
          }
          return;
        }
        if (data.status === "expired") {
          setPhase("failed");
          return;
        }
        count++;
        if (count >= MAX_ATTEMPTS) {
          setPhase("timeout");
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        setPhase("failed");
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-white text-[var(--kb-fg)]">
      <Nav />
      <main className="pt-32 px-5 sm:px-10 lg:px-14 max-w-3xl mx-auto pb-24">
        {phase === "checking" && (
          <div data-testid="success-checking" className="flex flex-col items-center text-center py-20">
            <Loader2 size={32} className="animate-spin kb-blue mb-6" />
            <div className="kb-mono-label kb-blue">CONFIRMING PAYMENT</div>
            <h1 className="font-serif text-4xl mt-2">Just a moment…</h1>
            <p className="text-[var(--kb-fg-2)] mt-2 max-w-md">
              We’re verifying your deposit with Stripe. Attempt {attempts + 1} / {MAX_ATTEMPTS}.
            </p>
            <div className="mt-8 h-px w-32 bg-[var(--kb-border)] kb-shimmer" />
          </div>
        )}

        {phase === "paid" && (
          <div data-testid="success-paid" className="text-center py-20">
            <CheckCircle2 size={40} className="kb-blue mx-auto mb-6" />
            <div className="kb-mono-label kb-blue">RESERVATION CONFIRMED</div>
            <h1 className="font-serif text-5xl mt-2 tracking-tight">
              Your KISUBIKA<br />is held.
            </h1>
            <p className="text-[var(--kb-fg-2)] mt-4 max-w-md mx-auto font-light">
              Thank you. Your £20 deposit is secured. We’ve emailed your receipt.
              Your bag will ship from London on the next drop.
            </p>

            <div className="mt-10 inline-flex flex-col gap-3 border border-[var(--kb-fg)] p-5 text-left">
              <div className="flex justify-between gap-12">
                <span className="kb-mono-label text-[var(--kb-fg-2)]">RESERVATION</span>
                <span className="kb-mono-label">{data?.reservation_id?.slice(0, 8).toUpperCase()}</span>
              </div>
              <div className="flex justify-between gap-12">
                <span className="kb-mono-label text-[var(--kb-fg-2)]">SESSION</span>
                <span className="kb-mono-label">{sessionId?.slice(-10).toUpperCase()}</span>
              </div>
              <div className="flex justify-between gap-12">
                <span className="kb-mono-label text-[var(--kb-fg-2)]">AMOUNT</span>
                <span className="kb-mono-label kb-blue">
                  £{((data?.amount_total || 0) / 100).toFixed(2)} {data?.currency?.toUpperCase()}
                </span>
              </div>
            </div>

            <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
              {data?.reservation_id && (
                <Link
                  to={`/r/${data.reservation_id}`}
                  className="kb-btn-primary"
                  data-testid="success-view-receipt"
                >
                  View receipt
                </Link>
              )}
              <Link to="/reservations" className="kb-btn-ghost" data-testid="success-view-reservations">
                All my reservations
              </Link>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="kb-btn-ghost"
              >
                <Instagram size={14} /> Share to Stories
              </a>
            </div>
          </div>
        )}

        {(phase === "failed" || phase === "timeout") && (
          <div data-testid="success-failed" className="text-center py-20">
            <AlertCircle size={32} className="text-[var(--kb-error)] mx-auto mb-6" />
            <div className="kb-mono-label" style={{ color: "var(--kb-error)" }}>
              {phase === "timeout" ? "STATUS PENDING" : "PAYMENT NOT CONFIRMED"}
            </div>
            <h1 className="font-serif text-4xl mt-2">
              {phase === "timeout" ? "Still processing" : "Something went wrong"}
            </h1>
            <p className="text-[var(--kb-fg-2)] mt-3 max-w-md mx-auto font-light">
              {phase === "timeout"
                ? "Your payment is still being processed. Check your email for confirmation, or look it up in My Bag."
                : "We couldn’t confirm your payment. Please try again or contact support."}
            </p>
            <div className="mt-8 flex justify-center gap-3 flex-wrap">
              <button onClick={() => navigate("/ar")} className="kb-btn-primary">
                Try again
              </button>
              <Link to="/reservations" className="kb-btn-ghost">
                Look up my bag
              </Link>
            </div>
          </div>
        )}
      </main>
      <PageFooter />
    </div>
  );
};

export default Success;
